/**
 * Design Lab Init Hook
 * React integration for the simplified init machine
 */

import { useMachine } from "@xstate/react";
import { useApolloClient } from "@design-lab/shared/graphql";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router";
import { useDesignLabSkipEmptyModal } from "@design-lab/shared/lib/config";
import { DesignUploadCookie } from "@design-lab/shared/lib/designUploadCookie";
import {
  actions,
  designLabInitMachine,
  guards,
  services,
  type DesignLabInitContext
} from "../machines/design-lab-init";

export type UseDesignLabInitReturn = {
  // State flags
  isEditMode: boolean;
  isReady: boolean;
  isLoading: boolean;
  isError: boolean;

  // Machine state
  state: string;
  isWaitingForLibrary: boolean;
  isWaitingForCanva: boolean;

  // Flow type
  flowType: DesignLabInitContext["flowType"];

  // Project data
  project: DesignLabInitContext["project"];
  latestProject: DesignLabInitContext["latestProject"];
  composition: DesignLabInitContext["composition"];

  // Upload state
  uploadSource: DesignLabInitContext["uploadSource"];
  uploadedFiles: DesignLabInitContext["uploadedFiles"];
  externalUploads: DesignLabInitContext["externalUploads"];
  targetSide: "front" | "back";

  // UI State
  showModal: boolean;
  modalAction: DesignLabInitContext["modalAction"];

  // Error
  error: DesignLabInitContext["error"];

  // Actions
  continueWithLatest: () => void;
  startNew: (source: "device" | "library" | "canva") => void;
  closeModal: () => void;
  selectFiles: (files: File[]) => void;
  handleLibrarySelection: () => void;
  handleCanvaComplete: (designUrl: string) => void;
  cancel: () => void;
  retry: () => void;
  reset: () => void;
};

export function useDesignLabInit(): UseDesignLabInitReturn {
  const apolloClient = useApolloClient();
  const navigate = useNavigate();

  // MN-5326: read the design_lab_skip_empty_modal flip and the guest upload
  // cookie ONCE at hook init. The machine input flips legacyAlwaysShowModal off
  // when the flip is on, so the StartDesigningModal can be skipped for users
  // with no saved content. The cookie is read inside try/catch — although
  // DesignUploadCookie.read() catches its own JSON/atob errors and returns
  // null, this guards against any future change in that contract crashing the
  // hook at init time.
  const skipEmptyModal = useDesignLabSkipEmptyModal();
  const hasCookieArtwork = useMemo(() => {
    try {
      return !!DesignUploadCookie.read();
    } catch {
      return false;
    }
  }, []);

  // Initialize machine with Apollo client
  const [state, send] = useMachine(
    designLabInitMachine.provide({
      // XState v5's .provide() expects actions typed with the machine's specific
      // context/event types which are too complex to satisfy at this call site.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      actions: actions as any,
      guards,
      actors: services
    }),
    {
      input: {
        apolloClient,
        navigate,
        // When flip is off, force hasCookieArtwork: false so cookie reads
        // can't influence the legacy code path.
        hasCookieArtwork: skipEmptyModal ? hasCookieArtwork : false,
        legacyAlwaysShowModal: !skipEmptyModal
      }
    }
  );

  const { context } = state;

  // Check for edit mode
  // Note: If edit mode is detected, the machine stops at 'editModeDetected'
  // The existing useEditMode hook should be used in the parent component
  const isEditMode = context.isEditMode || state.matches("editModeDetected");

  // State flags
  const isReady = state.matches("ready");
  // We're loading if we're actively fetching data, not when waiting for user input
  const isLoading =
    state.matches("loadingProject") ||
    state.matches("creatingProject") ||
    state.matches("checkingLatestProject") ||
    state.matches("restoringProject") ||
    state.matches("processingUpload");
  const isError = state.matches("error");

  // Actions
  const continueWithLatest = useCallback(() => {
    send({ type: "CONTINUE_WITH_LATEST" });
  }, [send]);

  const startNew = useCallback(
    (source: "device" | "library" | "canva") => {
      send({ type: "START_NEW", source });
    },
    [send]
  );

  const closeModal = useCallback(() => {
    send({ type: "CLOSE_MODAL" });
  }, [send]);

  const selectFiles = useCallback(
    (files: File[]) => {
      send({ type: "FILES_SELECTED", files });
    },
    [send]
  );

  const handleLibrarySelection = useCallback(() => {
    send({ type: "LIBRARY_SELECTION" });
  }, [send]);

  const handleCanvaComplete = useCallback(
    (designUrl: string) => {
      send({ type: "CANVA_COMPLETE", designUrl });
    },
    [send]
  );

  const cancel = useCallback(() => {
    send({ type: "CANCEL" });
  }, [send]);

  const retry = useCallback(() => {
    send({ type: "RETRY" });
  }, [send]);

  const reset = useCallback(() => {
    send({ type: "RESET" });
  }, [send]);

  return {
    // State flags
    isEditMode,
    isReady,
    isLoading,
    isError,

    // Machine state - expose for UI to check specific states
    state: state.value as string,
    isWaitingForLibrary: state.matches("processingUpload.openingLibrary"),
    isWaitingForCanva: state.matches("processingUpload.openingCanva"),

    // Flow type
    flowType: context.flowType,

    // Project data
    project: context.project,
    latestProject: context.latestProject,
    composition: context.composition,

    // Upload state
    uploadSource: context.uploadSource,
    uploadedFiles: context.uploadedFiles,
    externalUploads: context.externalUploads,
    targetSide: context.targetSide,

    // UI State
    showModal: context.showModal,
    modalAction: context.modalAction,

    // Error
    error: context.error,

    // Actions
    continueWithLatest,
    startNew,
    closeModal,
    selectFiles,
    handleLibrarySelection,
    handleCanvaComplete,
    cancel,
    retry,
    reset
  };
}
